import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Bell, BellOff, Package, DollarSign, MessageCircle, AlertTriangle, Clock, Check, ChevronRight, Settings } from 'lucide-react';
import { notificationService, PushNotification } from '../../services/notification.service';

type Screen = string;

interface NotificationScreenProps {
  onBack: () => void;
  onNavigate: (screen: Screen, data?: any) => void;
}

export default function NotificationScreen({ onBack, onNavigate }: NotificationScreenProps) {
  const [notifications, setNotifications] = useState<PushNotification[]>([]);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [isEnabled, setIsEnabled] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState<NotificationPermission>('default');

  useEffect(() => {
    // Load notification history
    loadNotifications();

    // Check permission status
    setPermissionStatus(notificationService.getPermissionStatus());
    setIsEnabled(notificationService.isEnabled());

    // Subscribe to new notifications
    const unsubscribe = notificationService.subscribe((notification) => {
      setNotifications(prev => [notification, ...prev]);
    });

    return () => unsubscribe();
  }, []);

  const loadNotifications = () => {
    const history = notificationService.getNotificationHistory();
    setNotifications(history);
  };

  const handleEnableNotifications = async () => {
    const token = await notificationService.requestPermission();
    if (token) {
      setIsEnabled(true);
      setPermissionStatus('granted');
      
      if (token === 'in-app-only') {
        // FCM failed but browser notifications are enabled
        alert('✅ Notifications enabled!\n\n⚠️ Note: Push notifications are using in-app mode only. You\'ll still receive notifications when the app is open.');
      }
    }
  };

  const handleNotificationClick = (notification: PushNotification) => {
    // Mark as read
    notificationService.markAsRead(notification.id);
    setNotifications(prev =>
      prev.map(n => n.id === notification.id ? { ...n, read: true } : n)
    );

    // Navigate based on notification type
    if (notification.data?.jobId) {
      onNavigate('job-detail', notification.data.jobId);
    } else if (notification.data?.screen) {
      onNavigate(notification.data.screen);
    }
  };

  const handleClearAll = () => {
    if (confirm('Clear all notifications?')) {
      notificationService.clearHistory();
      setNotifications([]);
    }
  };

  const handleMarkAllRead = () => {
    const updated = notifications.map(n => {
      if (!n.read) {
        notificationService.markAsRead(n.id);
      }
      return { ...n, read: true };
    });
    setNotifications(updated);
  };

  const handleTestNotification = async () => {
    await notificationService.sendTestNotification();
  };

  const filteredNotifications = filter === 'unread'
    ? notifications.filter(n => !n.read)
    : notifications;

  const unreadCount = notifications.filter(n => !n.read).length;

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'new-job':
        return <Package className="w-5 h-5" />;
      case 'earnings-update':
        return <DollarSign className="w-5 h-5" />;
      case 'message':
        return <MessageCircle className="w-5 h-5" />;
      case 'system':
      case 'shift-reminder':
      case 'promotion':
        return <Bell className="w-5 h-5" />;
      default:
        return <AlertTriangle className="w-5 h-5" />;
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'new-job':
        return 'bg-blue-500';
      case 'earnings-update':
        return 'bg-green-500';
      case 'message':
        return 'bg-purple-500';
      case 'job-cancelled':
        return 'bg-red-500';
      case 'promotion':
        return 'bg-yellow-500';
      default:
        return 'bg-gray-500';
    }
  };

  const formatTime = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - new Date(date).getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return new Date(date).toLocaleDateString();
  };

  return (
    <div className="absolute inset-0 flex flex-col bg-gray-50 overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={onBack}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <ChevronRight className="w-6 h-6 rotate-180" />
              </button>
              <div>
                <h1 className="text-xl font-bold">Notifications</h1>
                {unreadCount > 0 && (
                  <p className="text-sm text-gray-600">{unreadCount} unread</p>
                )}
              </div>
            </div>
            <button
              onClick={handleClearAll}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              Clear All
            </button>
          </div>
        </div>

        {/* Permission Banner */}
        {!isEnabled && (
          <div className="px-4 py-3 bg-blue-50 border-t border-blue-100">
            <div className="flex items-start gap-3">
              <BellOff className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-900">
                  Enable push notifications
                </p>
                <p className="text-xs text-blue-700 mt-1">
                  Stay updated with new jobs, messages, and earnings
                </p>
              </div>
              <button
                onClick={handleEnableNotifications}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                Enable
              </button>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="px-4 py-3 border-t border-gray-200">
          <div className="flex gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === 'all'
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              All ({notifications.length})
            </button>
            <button
              onClick={() => setFilter('unread')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === 'unread'
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Unread ({unreadCount})
            </button>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="ml-auto px-4 py-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Mark all read
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Notifications List */}
      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
        {filteredNotifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full p-8 text-center">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Bell className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {filter === 'unread' ? 'All caught up!' : 'No notifications yet'}
            </h3>
            <p className="text-gray-600 mb-6">
              {filter === 'unread'
                ? "You've read all your notifications"
                : 'New notifications will appear here'}
            </p>
            {isEnabled && (
              <button
                onClick={handleTestNotification}
                className="px-4 py-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Send test notification
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredNotifications.map((notification, index) => (
              <motion.button
                key={notification.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={() => handleNotificationClick(notification)}
                className={`w-full px-4 py-4 text-left hover:bg-gray-50 transition-colors ${
                  !notification.read ? 'bg-blue-50' : 'bg-white'
                }`}
              >
                <div className="flex gap-3">
                  {/* Icon */}
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${getNotificationColor(
                      notification.type
                    )} text-white`}
                  >
                    {getNotificationIcon(notification.type)}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h3
                        className={`font-semibold ${
                          !notification.read ? 'text-gray-900' : 'text-gray-700'
                        }`}
                      >
                        {notification.title}
                      </h3>
                      {!notification.read && (
                        <div className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0 mt-1.5" />
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                      {notification.body}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <Clock className="w-3 h-3" />
                      {formatTime(notification.timestamp)}
                    </div>
                  </div>

                  {/* Chevron */}
                  <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0 mt-2" />
                </div>
              </motion.button>
            ))}
          </div>
        )}
      </div>

      {/* Settings Footer */}
      {isEnabled && (
        <div className="bg-white border-t border-gray-200 p-4">
          <button
            onClick={() => onNavigate('advanced-settings')}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <Settings className="w-5 h-5" />
            <span className="font-medium">Notification Settings</span>
          </button>
        </div>
      )}
    </div>
  );
}
